// Single source of truth for the live webinar.
//
// Both /webinar (registration funnel) and /webinar-thank-you (confirmation
// + add-to-calendar) import from here so the date never drifts between pages.
// To run a new webinar, change WEBINAR_DATE + (if needed) WEBINAR_DURATION_MINUTES
// in one place and every surface — title bar, hero pill, countdown, calendar
// invites, ICS file — updates together.

// ─── CONFIG ────────────────────────────────────────────────────────────────
// Webinar date in local time (Europe/Vienna in summer = +02:00 CEST).
export const WEBINAR_DATE = new Date('2026-08-11T18:00:00+02:00');
export const WEBINAR_DURATION_MINUTES = 180;
export const WEBINAR_FORMAT = 'live online';

// True once the scheduled date has passed. Date-display surfaces fall back to
// WEBINAR_TBD_LABEL so a past date never sits on the page advertising a dead
// event — the next date gets set here in webinar.js and everything updates.
export function isWebinarPast() {
  return WEBINAR_DATE.getTime() < Date.now();
}
export const WEBINAR_TBD_LABEL = 'Nächster Termin folgt in Kürze';

// WhatsApp reminder/community group new registrants are invited into on the
// thank-you page. Real invite link — the thank-you WhatsApp section renders
// once this is set (a REPLACE_WITH_REAL_INVITE placeholder withholds it).
export const WHATSAPP_GROUP_URL = 'https://chat.whatsapp.com/JyllxiWt7TS6SZQbst9PNR';
// Calendar-entry title (Google / Outlook / .ics). Mirrors the big hero
// headline on the /webinar landing page so the calendar reminds attendees of
// the exact promise they signed up for (recognition → fewer no-shows). The
// full two-sentence headline is too long for a calendar chip, so this is its
// core promise, prefixed with "Webinar:" for instant recognition. Keep it in
// sync if the hero headline (WebinarPage.jsx) changes.
export const WEBINAR_TITLE = 'Webinar: Wie du dir mit KI dein eigenes App-Business baust';
export const WEBINAR_DESCRIPTION =
  'Live-Webinar der AI App Academy. Wie du mit KI in 6 klaren Schritten deine eigene App entwickelst und im Store launchst. Ohne Programmierkenntnisse. Ohne teure Agentur.\n\nHier teilnehmen: https://app-academy.ai/live\n\nMehr Infos: https://app-academy.ai/webinar';
// The location field renders as a clickable link in Google Calendar, Apple
// Calendar and Outlook when it contains a full URL — that becomes the
// one-click "join now" jump for registered attendees at start time.
export const WEBINAR_LOCATION = 'https://app-academy.ai/live';
export const WEBINAR_PAGE_URL = 'https://app-academy.ai/webinar';
export const WEBINAR_LIVE_URL = 'https://app-academy.ai/live';

// Zoom-Cloud-Aufzeichnung des letzten Webinars, eingebettet auf /replay.
// Bewusst die /rec/share/-URL (nicht die /rec/play/-URL, auf die Zoom danach
// weiterleitet): der share-Token ist der dauerhaft teilbare Link, der
// play-Token wird pro Aufruf neu vergeben und wäre hart verdrahtet ungültig.
// Neue Aufzeichnung = nur diese Konstante tauschen.
export const WEBINAR_REPLAY_URL =
  'https://us06web.zoom.us/rec/share/zYigv4X5xIo2B_YSvt8eNwS_9ry73NTbu4K0bdGdgkMr-_2ImivMPFa-u37TB4vW.GJNSgXByUcYsELMz';

// Calendly URL for the Strategiegespräch-Slot we offer to webinar attendees
// in-broadcast ("Geht jetzt auf app-academy.ai/live …"). Separate Event-Type
// from the main /-page Infogespräch so we can track + segment bookings that
// came out of the webinar funnel.
export const WEBINAR_STRATEGY_CALL_URL =
  'https://calendly.com/dominik-gross/app-academy-infogesprach-webinar';

// ─── FORMATTERS ────────────────────────────────────────────────────────────
export const DATE_FMT = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

export const TIME_FMT = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
});

// ─── DERIVED TIMES ─────────────────────────────────────────────────────────
function getEndDate() {
  return new Date(WEBINAR_DATE.getTime() + WEBINAR_DURATION_MINUTES * 60 * 1000);
}

// Compact UTC string used by Google/Yahoo/ICS: "20260702T090000Z".
function toUtcCompact(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    'Z'
  );
}

// ISO with seconds, no milliseconds — Outlook's preferred shape.
function toUtcIso(d) {
  return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

// ─── CALENDAR URL BUILDERS ─────────────────────────────────────────────────
// Each builder returns a URL the user can click to add the event in their
// chosen service. UTC compact / ISO formats are what each service expects.

export function getGoogleCalendarUrl() {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: WEBINAR_TITLE,
    dates: `${toUtcCompact(WEBINAR_DATE)}/${toUtcCompact(getEndDate())}`,
    details: WEBINAR_DESCRIPTION,
    location: WEBINAR_LOCATION,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// Outlook (Microsoft 365 / office.com). Works for both personal Microsoft
// accounts and work/school accounts that authenticate through office.com.
export function getOutlook365CalendarUrl() {
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: WEBINAR_TITLE,
    startdt: toUtcIso(WEBINAR_DATE),
    enddt: toUtcIso(getEndDate()),
    body: WEBINAR_DESCRIPTION,
    location: WEBINAR_LOCATION,
  });
  return `https://outlook.office.com/calendar/0/deeplink/compose?${params.toString()}`;
}

// ─── ICS (Apple Calendar, Outlook desktop, fallback) ───────────────────────
// Escape per RFC 5545: backslash, comma, semicolon and newline get escaped.
function icsEscape(s) {
  return String(s)
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

export function getIcsContent() {
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//AI App Academy//Webinar//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:webinar-${WEBINAR_DATE.getTime()}@app-academy.ai`,
    `DTSTAMP:${toUtcCompact(new Date())}`,
    `DTSTART:${toUtcCompact(WEBINAR_DATE)}`,
    `DTEND:${toUtcCompact(getEndDate())}`,
    `SUMMARY:${icsEscape(WEBINAR_TITLE)}`,
    `DESCRIPTION:${icsEscape(WEBINAR_DESCRIPTION)}`,
    `LOCATION:${icsEscape(WEBINAR_LOCATION)}`,
    `URL:${WEBINAR_PAGE_URL}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
}

// Trigger an .ics download. Uses a Blob + objectURL so the file gets a real
// filename (data: URLs land as "untitled" in some browsers). Object URL is
// revoked on the next tick — the download has already kicked off by then.
export function downloadIcs() {
  const blob = new Blob([getIcsContent()], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ai-app-academy-webinar.ics';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
